import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import { getBenefitIcon, BenefitIconType } from '../../components/shared/BenefitIcons';
import useScrollToTop from '../../hooks/useScrollToTop';

const Belgium: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  useScrollToTop();

  const benefits: { icon: BenefitIconType; text: string }[] = [
    { icon: 'flight', text: t('residency.belgium.benefits.travel') },
    { icon: 'work', text: t('residency.belgium.benefits.work') },
    { icon: 'security', text: t('residency.belgium.benefits.healthcare') },
    { icon: 'school', text: t('residency.belgium.benefits.quality') },
    { icon: 'timeline', text: t('residency.belgium.benefits.business') },
    { icon: 'family', text: t('residency.belgium.benefits.family') },
  ];

  const requirements = [
    t('residency.belgium.requirements.investment'),
    t('residency.belgium.requirements.criminal'),
    t('residency.belgium.requirements.passport'),
    t('residency.belgium.requirements.financial'),
    t('residency.belgium.requirements.fee'),
    t('residency.belgium.requirements.insurance'),
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))',
          backgroundColor: '#2196f3',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '40vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" component="h1" gutterBottom>
              {t('residency.belgium.hero.title')}
            </Typography>
            <Typography variant="h5" gutterBottom>
              {t('residency.belgium.hero.subtitle')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Program Overview */}
      <Container sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography variant="h4" gutterBottom align="center">
                {t('residency.belgium.overview.title')}
              </Typography>
              <Typography variant="body1" paragraph align="center">
                {t('residency.belgium.overview.description1')}
              </Typography>
              <Typography variant="body1" paragraph align="center">
                {t('residency.belgium.overview.description2')}
              </Typography>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            {t('residency.belgium.benefits.title')}
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ color: 'primary.main', mr: 2 }}>
                          {getBenefitIcon(benefit.icon)}
                        </Box>
                        <Typography variant="h6" component="h3">
                          {benefit.text}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Requirements Section */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          {t('residency.belgium.requirements.title')}
        </Typography>
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {t('residency.belgium.requirements.investmentTitle')}
                  </Typography>
                  <List>
                    {requirements.map((requirement, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={requirement} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {t('residency.belgium.timeline.title')}
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary={t('residency.belgium.timeline.initial.title')}
                        secondary={t('residency.belgium.timeline.initial.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('residency.belgium.timeline.residence.title')}
                        secondary={t('residency.belgium.timeline.residence.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('residency.belgium.timeline.permanent.title')}
                        secondary={t('residency.belgium.timeline.permanent.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('residency.belgium.timeline.citizenship.title')}
                        secondary={t('residency.belgium.timeline.citizenship.description')}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Belgium; 